import { useState, useCallback } from 'react';

/**
 * Tracks per-file transfer progress in a keyed map:
 *   { [key]: { loaded, total, status: 'active' | 'done' | 'error' } }
 * All setters use functional updates so parallel per-key callbacks don't clobber.
 */
const useFileTransferProgress = () => {
  const [progress, setProgress] = useState({});

  const start = useCallback((key, total = 0) => {
    setProgress((prev) => ({ ...prev, [key]: { loaded: 0, total, status: 'active' } }));
  }, []);

  const update = useCallback((key, loaded, total) => {
    setProgress((prev) => {
      const entry = prev[key] || { total: 0 };
      return {
        ...prev,
        [key]: { loaded, total: total !== undefined && total !== null ? total : entry.total, status: 'active' },
      };
    });
  }, []);

  const complete = useCallback((key) => {
    setProgress((prev) => (prev[key] ? { ...prev, [key]: { ...prev[key], status: 'done' } } : prev));
  }, []);

  const fail = useCallback((key) => {
    setProgress((prev) => {
      const entry = prev[key] || { loaded: 0, total: 0 };
      return { ...prev, [key]: { ...entry, status: 'error' } };
    });
  }, []);

  const reset = useCallback((key) => {
    setProgress((prev) => {
      if (key === undefined) return {};
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  return { progress, start, update, complete, fail, reset };
};

export default useFileTransferProgress;
